/**
 * Internal QA: the levers that make the onboarding wizard's ERROR and SLOW
 * states reachable (EDUS-2855).
 *
 * Every other hard-to-reach wizard state has a lever — a forced verdict, a
 * seeded licence, a forced DWD status, an armed sync outcome. The error and
 * fallback screens had none, because they render from a caught HTTP failure:
 * nothing in the console could produce one, and the browser cannot fail the
 * call on the server's behalf either. Six of the nine screens with no QA route
 * were this one problem wearing six hats.
 *
 * Two keys, both read by `QaForcedFailureGuard` on the onboarding controller
 * and written only by the platform-admin QA controller (test domains only, ~1h
 * TTL). Defined here so the writer and the reader share one source and cannot
 * drift on the key strings.
 */

/**
 * The onboarding requests QA can force to fail, named by the SCREEN each one
 * unblocks rather than by route — the console lists them, and a tester picking
 * a lever is thinking "I need the Step 2 load error", not "I need
 * GET /onboarding/environment".
 *
 * `sync-data` is deliberately absent: `sync-fail-override` already fails that
 * kickoff, and a second lever for the same screen is how two levers end up
 * disagreeing about which one is armed.
 */
export const QA_FORCEABLE_ENDPOINTS = [
  'progress',
  'agreement-accept',
  'environment',
  'verify-delegation',
  'invite-validate',
  'invite-send',
] as const;

export type QaForceableEndpoint = (typeof QA_FORCEABLE_ENDPOINTS)[number];

/** What each lever gets QA to, for the console's toggle list. */
export const QA_FORCEABLE_ENDPOINT_LABELS: Record<
  QaForceableEndpoint,
  string
> = {
  progress: 'Wizard shell load error (GET progress)',
  'agreement-accept': 'Step 1 — acceptance failed (POST agreement/accept)',
  environment: 'Step 2 — load error (GET environment)',
  'verify-delegation': 'Step 3 — verification failed (POST verify-delegation)',
  'invite-validate': 'Step 5 — address check failed (POST invite-users/validate)',
  'invite-send': 'Step 5 — send failed (POST invite-users)',
};

/**
 * Marks one onboarding endpoint as forced-to-fail for one test customer.
 *
 * Keyed by CUSTOMER, not domain (unlike {@link qaClientIdFailKey}): all six
 * routes sit behind `OnboardingSaGuard` and therefore always carry wizard
 * claims with a resolved `customerId`. One key per endpoint, so the six are
 * independently armable — a single multi-valued key would mean arming one lever
 * silently disarmed the others.
 */
export function qaEndpointFailKey(
  customerId: number,
  endpoint: QaForceableEndpoint
): string {
  return `qa_endpoint_fail_${customerId}_${endpoint}`;
}

/**
 * Holds a delay in milliseconds applied to EVERY `/onboarding` request for one
 * test customer, so the transient spinners — which on a local or sandbox tenant
 * flash past in well under a second — can actually be looked at.
 *
 * Global rather than per-endpoint on purpose. Forcing a failure is a targeted
 * question ("show me THIS error screen") so it must name the endpoint; a delay
 * is ambient ("let me see the spinners") and nobody cares which route was slow.
 */
export function qaSpinnerDelayKey(customerId: number): string {
  return `qa_spinner_delay_${customerId}`;
}

/**
 * Upper bound on the spinner delay, enforced by the DTO.
 *
 * Past roughly this point you stop testing the spinner and start testing the
 * stack's HTTP timeouts — the browser, Cloud Run and any proxy in between all
 * have opinions, and a lever that trips them produces a network error rather
 * than the loading state it was set to show.
 */
export const QA_MAX_SPINNER_DELAY_MS = 30_000;
